import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Divider
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DatePicker, TimePicker, DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';

const AdvancedFiltersModal = ({
  open,
  onClose,
  columns,
  currentFilters,
  onApplyFilters
}) => {
  const [filterRows, setFilterRows] = useState([]);

  // Operadores de comparación disponibles
  const comparisonOperators = [
    { value: 'contains', label: 'Contiene' },
    { value: 'equals', label: 'Igual a' },
    { value: 'starts_with', label: 'Comienza por' },
    { value: 'ends_with', label: 'Termina por' },
    { value: 'not_contains', label: 'No contiene' },
    { value: 'in', label: 'En lista' },
    { value: 'greater_than', label: 'Mayor que' },
    { value: 'less_than', label: 'Menor que' },
    { value: 'greater_equal', label: 'Mayor o igual que' },
    { value: 'less_equal', label: 'Menor o igual que' },
    { value: 'between', label: 'Entre' },
    { value: 'is_null', label: 'Es nulo' },
    { value: 'is_not_null', label: 'No es nulo' }
  ];

  // Obtener campos filtrables de las columnas
  const getFilterableFields = () => {
    return columns
      .filter(col => col.name && col.filter !== false)
      .map(col => ({
        value: col.name,
        label: col.label,
        type: col.filterType || 'text'
      }));
  };

  // Función para formatear fecha y hora en zona local
  const formatLocalDateTime = (date) => {
    if (!date) return null;

    // Obtener año, mes, día, hora, minuto en zona local
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    // Formato: YYYY-MM-DD HH:MM:SS
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Inicializar filtros desde el estado actual
  useEffect(() => {
    if (open && currentFilters) {
      const rows = Object.entries(currentFilters).map(([field, filterData]) => {
        const column = columns.find(col => col.name === field);
        const fieldType = column?.filterType || 'text';

        if (filterData.operator) {
          // Filtro con operador
          return {
            id: Math.random().toString(36).substr(2, 9),
            field: field,
            operator: filterData.operator,
            value: Array.isArray(filterData.value) ? filterData.value.join(', ') : filterData.value,
            type: fieldType
          };
        } else if (filterData.from || filterData.to) {
          // Filtro de rango
          return {
            id: Math.random().toString(36).substr(2, 9),
            field: field,
            operator: 'between',
            value: `${filterData.from || ''} - ${filterData.to || ''}`,
            type: fieldType,
            // Para fechas, convertir de string a Date
            fromValue: filterData.from ? new Date(filterData.from) : null,
            toValue: filterData.to ? new Date(filterData.to) : null
          };
        }
        return null;
      }).filter(Boolean);

      setFilterRows(rows);
    } else if (open) {
      setFilterRows([]);
    }
  }, [open, currentFilters, columns]);

  // Agregar nueva fila de filtro
  const addFilterRow = () => {
    const newRow = {
      id: Math.random().toString(36).substr(2, 9),
      field: '',
      operator: 'contains',
      value: '',
      type: 'text',
      fromValue: null,
      toValue: null
    };
    setFilterRows([...filterRows, newRow]);
  };

  // Eliminar fila de filtro
  const removeFilterRow = (id) => {
    setFilterRows(filterRows.filter(row => row.id !== id));
  };

  // Actualizar valor de una fila
  const updateFilterRow = (id, field, value) => {
    setFilterRows(filterRows.map(row => {
      if (row.id === id) {
        const updatedRow = { ...row, [field]: value };

        // Si cambió el campo, actualizar el tipo
        if (field === 'field') {
          const column = columns.find(col => col.name === value);
          updatedRow.type = column?.filterType || 'text';
        }

        return updatedRow;
      }
      return row;
    }));
  };

  // Aplicar filtros
  const applyFilters = () => {
    const newFilters = {};

    filterRows.forEach(row => {
      if (row.field && row.operator && (row.value || ['is_null', 'is_not_null'].includes(row.operator))) {
        const filterData = {};

        if (row.operator === 'between') {
          // Manejar filtro de rango según el tipo
          if (row.type === 'date') {
            if (row.fromValue) filterData.from = row.fromValue.toISOString().split('T')[0];
            if (row.toValue) filterData.to = row.toValue.toISOString().split('T')[0];
          } else if (row.type === 'time') {
            if (row.fromValue) filterData.from = row.fromValue.toTimeString().split(' ')[0];
            if (row.toValue) filterData.to = row.toValue.toTimeString().split(' ')[0];
          } else if (row.type === 'datetime') {
            if (row.fromValue) filterData.from = formatLocalDateTime(row.fromValue);
            if (row.toValue) filterData.to = formatLocalDateTime(row.toValue);
          } else if (row.type === 'number') {
            const [from, to] = row.value.split('-').map(v => v.trim());
            if (from) filterData.from = parseFloat(from);
            if (to) filterData.to = parseFloat(to);
          } else {
            // Para otros tipos, usar el formato original
            const [from, to] = row.value.split('-').map(v => v.trim());
            if (from) filterData.from = from;
            if (to) filterData.to = to;
          }
        } else if (row.operator === 'in') {
          // Manejar filtro de lista
          const values = row.value.split(',').map(v => v.trim()).filter(v => v.length > 0);
          filterData.value = values;
          filterData.operator = 'in';
        } else if (['is_null', 'is_not_null'].includes(row.operator)) {
          // Manejar filtros de nulos
          filterData.operator = row.operator;
        } else {
          // Manejar filtros normales
          filterData.value = row.value;
          filterData.operator = row.operator;
        }

        newFilters[row.field] = filterData;
      }
    });

    onApplyFilters(newFilters);
    onClose();
  };

  // Limpiar todos los filtros
  const clearAllFilters = () => {
    setFilterRows([]);
    onApplyFilters({});
    onClose();
  };

  // Obtener operadores disponibles según el tipo de campo
  const getOperatorsForType = (type) => {
    switch (type) {
      case 'date':
      case 'datetime':
        return comparisonOperators.filter(op =>
          ['equals', 'greater_than', 'less_than', 'greater_equal', 'less_equal', 'between', 'is_null', 'is_not_null'].includes(op.value)
        );
      case 'time':
        return comparisonOperators.filter(op =>
          ['equals', 'greater_than', 'less_than', 'greater_equal', 'less_equal', 'between', 'is_null', 'is_not_null'].includes(op.value)
        );
      case 'number':
        return comparisonOperators.filter(op =>
          ['equals', 'greater_than', 'less_than', 'greater_equal', 'less_equal', 'between', 'is_null', 'is_not_null'].includes(op.value)
        );
      case 'boolean':
        return comparisonOperators.filter(op =>
          ['equals', 'is_null', 'is_not_null'].includes(op.value)
        );
      case 'list':
        return comparisonOperators.filter(op =>
          ['in', 'is_null', 'is_not_null'].includes(op.value)
        );
      default: // text
        return comparisonOperators.filter(op =>
          ['contains', 'equals', 'starts_with', 'ends_with', 'not_contains', 'in', 'is_null', 'is_not_null'].includes(op.value)
        );
    }
  };

  // Renderizar campo de valor según el tipo y operador
  const renderValueField = (row) => {
    if (['is_null', 'is_not_null'].includes(row.operator)) {
      return (
        <TextField
          size="small"
          value=""
          disabled
          placeholder="No aplica"
          fullWidth
        />
      );
    }

    // Para filtros de rango, usar componentes específicos según el tipo
    if (row.operator === 'between') {
      switch (row.type) {
        case 'date':
          return (
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <Stack spacing={1}>
                <DatePicker
                  label="Desde"
                  value={row.fromValue}
                  onChange={(value) => updateFilterRow(row.id, 'fromValue', value)}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
                <DatePicker
                  label="Hasta"
                  value={row.toValue}
                  onChange={(value) => updateFilterRow(row.id, 'toValue', value)}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </Stack>
            </LocalizationProvider>
          );

        case 'time':
          return (
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <Stack spacing={1}>
                <TimePicker
                  label="Desde"
                  value={row.fromValue}
                  onChange={(value) => updateFilterRow(row.id, 'fromValue', value)}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
                <TimePicker
                  label="Hasta"
                  value={row.toValue}
                  onChange={(value) => updateFilterRow(row.id, 'toValue', value)}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </Stack>
            </LocalizationProvider>
          );

        case 'datetime':
          return (
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <Stack spacing={1}>
                <DateTimePicker
                  label="Desde"
                  value={row.fromValue}
                  onChange={(value) => updateFilterRow(row.id, 'fromValue', value)}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
                <DateTimePicker
                  label="Hasta"
                  value={row.toValue}
                  onChange={(value) => updateFilterRow(row.id, 'toValue', value)}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </Stack>
            </LocalizationProvider>
          );

        case 'number':
          return (
            <Stack spacing={1}>
              <TextField
                label="Desde"
                type="number"
                value={row.value.split('-')[0]?.trim() || ''}
                onChange={(e) => {
                  const toValue = row.value.split('-')[1]?.trim() || '';
                  updateFilterRow(row.id, 'value', `${e.target.value} - ${toValue}`);
                }}
                size="small"
                fullWidth
              />
              <TextField
                label="Hasta"
                type="number"
                value={row.value.split('-')[1]?.trim() || ''}
                onChange={(e) => {
                  const fromValue = row.value.split('-')[0]?.trim() || '';
                  updateFilterRow(row.id, 'value', `${fromValue} - ${e.target.value}`);
                }}
                size="small"
                fullWidth
              />
            </Stack>
          );

        default:
          return (
            <TextField
              size="small"
              value={row.value}
              onChange={(e) => updateFilterRow(row.id, 'value', e.target.value)}
              placeholder="valor1 - valor2"
              helperText="Separar con guión (-)"
              fullWidth
            />
          );
      }
    }

    // Para filtros booleanos
    if (row.type === 'boolean' && row.operator === 'equals') {
      const column = columns.find(col => col.name === row.field);
      const booleanOptions = column?.booleanOptions || { true: 'Sí', false: 'No' };

      return (
        <FormControl size="small" fullWidth>
          <InputLabel>Valor</InputLabel>
          <Select
            value={row.value}
            onChange={(e) => updateFilterRow(row.id, 'value', e.target.value)}
            label="Valor"
          >
            {Object.entries(booleanOptions).map(([key, label]) => (
              <MenuItem key={key} value={key === 'true' ? 'true' : 'false'}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    // Para filtros de lista
    if (row.operator === 'in') {
      return (
        <TextField
          size="small"
          value={row.value}
          onChange={(e) => updateFilterRow(row.id, 'value', e.target.value)}
          placeholder="valor1, valor2, valor3..."
          helperText="Separar valores con coma"
          fullWidth
          multiline
          rows={2}
        />
      );
    }

    // Para filtros de números
    if (row.type === 'number') {
      return (
        <TextField
          size="small"
          type="number"
          value={row.value}
          onChange={(e) => updateFilterRow(row.id, 'value', e.target.value)}
          placeholder="Ingrese el valor..."
          fullWidth
        />
      );
    }

    // Para filtros de texto (por defecto)
    return (
      <TextField
        size="small"
        value={row.value}
        onChange={(e) => updateFilterRow(row.id, 'value', e.target.value)}
        placeholder="Ingrese el valor..."
        fullWidth
      />
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="advanced-filters-modal"
    >
      <Box sx={{
        position: 'absolute',
        top: '5%',
        left: '5%',
        right: '5%',
        bottom: '5%',
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 24,
        p: 3,
        overflow: 'auto'
      }}>
        <Typography variant="h5" gutterBottom>
          Filtros Avanzados
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure filtros personalizados para la tabla. Puede agregar múltiples condiciones.
        </Typography>

        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="20%">Campo</TableCell>
                <TableCell width="20%">Comparación</TableCell>
                <TableCell width="50%">Valor</TableCell>
                <TableCell width="10%" align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filterRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Campo</InputLabel>
                      <Select
                        value={row.field}
                        onChange={(e) => updateFilterRow(row.id, 'field', e.target.value)}
                        label="Campo"
                      >
                        {getFilterableFields().map((field) => (
                          <MenuItem key={field.value} value={field.value}>
                            {field.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>

                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Comparación</InputLabel>
                      <Select
                        value={row.operator}
                        onChange={(e) => updateFilterRow(row.id, 'operator', e.target.value)}
                        label="Comparación"
                      >
                        {getOperatorsForType(row.type).map((op) => (
                          <MenuItem key={op.value} value={op.value}>
                            {op.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>

                  <TableCell>
                    {renderValueField(row)}
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => removeFilterRow(row.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {filterRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No hay filtros configurados. Haga clic en "Agregar Filtro" para comenzar.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addFilterRow}
            >
              Agregar Filtro
            </Button>

            <Button
              variant="outlined"
              color="error"
              onClick={clearAllFilters}
            >
              Limpiar Todo
            </Button>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={onClose}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={applyFilters}
            >
              Aplicar Filtros
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};

AdvancedFiltersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
  currentFilters: PropTypes.object,
  onApplyFilters: PropTypes.func.isRequired,
};

export default AdvancedFiltersModal;
